import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import {
  findPendingMessageIndex,
  findListRowIndexByThread,
  dedupeConversationsByContactInbox,
  getListRowByThreadConversationId,
  isActiveThreadConversationUpdate,
  shouldRefreshThreadListRowActivity,
  shouldAdoptThreadListRowId,
} from './helpers';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.ALL,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  appliedFiltersSortBy: null,
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
  pinnedContactInboxIds: [],
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

const THREAD_ACTIVE_STATUSES = ['open', 'pending', 'snoozed'];

const applyThreadListActivity = (listRow, conversation = {}) => {
  const nextThreadActivity = Math.max(
    listRow.thread_last_activity_at || 0,
    conversation.thread_last_activity_at || 0,
    conversation.last_activity_at || 0,
    listRow.last_activity_at || 0
  );

  return {
    thread_last_activity_at: nextThreadActivity,
    last_activity_at: nextThreadActivity,
    timestamp: nextThreadActivity,
  };
};

const resolveThreadActiveConversationId = (incoming, existing) => {
  if (THREAD_ACTIVE_STATUSES.includes(incoming.status)) {
    return incoming.id;
  }

  return incoming.active_conversation_id ?? existing.active_conversation_id;
};
// mutations
export const mutations = {
  [types.SET_PINNED_CONTACT_INBOX_IDS](_state, contactInboxIds) {
    _state.pinnedContactInboxIds = contactInboxIds.map(Number);
  },
  [types.SET_ALL_CONVERSATION](_state, payload) {
    const conversationList = Array.isArray(payload)
      ? payload
      : payload.conversationList;
    const dedupeThreadRows = Array.isArray(payload)
      ? false
      : payload.dedupeThreadRows;

    let newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      let incomingConversation = conversation;

      if (dedupeThreadRows && conversation.contact_inbox_id) {
        const selectedThreadRow = newAllConversations.find(
          row =>
            Number(row.contact_inbox_id) ===
              Number(conversation.contact_inbox_id) &&
            row.id === _state.selectedChatId
        );

        newAllConversations = newAllConversations.filter(
          row =>
            row.contact_inbox_id !== conversation.contact_inbox_id ||
            row.id === conversation.id
        );

        // Opening a historical thread conversation selects a non-representative
        // id; the list fetch then replaces that row. Keep selection + messages.
        if (selectedThreadRow && selectedThreadRow.id !== conversation.id) {
          incomingConversation = {
            ...conversation,
            ...(selectedThreadRow.dataFetched &&
            selectedThreadRow.messages?.length
              ? {
                  messages: selectedThreadRow.messages,
                  thread_separators: selectedThreadRow.thread_separators,
                  dataFetched: selectedThreadRow.dataFetched,
                  allMessagesLoaded: selectedThreadRow.allMessagesLoaded,
                  hasNewerMessages: selectedThreadRow.hasNewerMessages,
                  newerMessagesCursor: selectedThreadRow.newerMessagesCursor,
                }
              : {}),
            active_conversation_id:
              conversation.active_conversation_id ??
              selectedThreadRow.active_conversation_id,
          };
          _state.selectedChatId = conversation.id;
        }
      }

      const indexInCurrentList =
        dedupeThreadRows && incomingConversation.contact_inbox_id
          ? newAllConversations.findIndex(
              row =>
                row.contact_inbox_id === incomingConversation.contact_inbox_id
            )
          : newAllConversations.findIndex(
              c => c.id === incomingConversation.id
            );

      if (indexInCurrentList < 0) {
        newAllConversations.push(incomingConversation);
      } else {
        const existingConversation = newAllConversations[indexInCurrentList];
        const isSelectedThreadRow =
          dedupeThreadRows &&
          existingConversation.id === _state.selectedChatId &&
          existingConversation.contact_inbox_id ===
            incomingConversation.contact_inbox_id;

        if (isSelectedThreadRow) {
          newAllConversations[indexInCurrentList] = {
            ...incomingConversation,
            id: existingConversation.id,
            allMessagesLoaded: existingConversation.allMessagesLoaded,
            messages: existingConversation.messages,
            dataFetched: existingConversation.dataFetched,
            hasNewerMessages: existingConversation.hasNewerMessages,
            newerMessagesCursor: existingConversation.newerMessagesCursor,
            thread_separators: existingConversation.thread_separators,
            active_conversation_id:
              incomingConversation.active_conversation_id ??
              existingConversation.active_conversation_id,
          };
        } else if (incomingConversation.id !== _state.selectedChatId) {
          const shouldReplace =
            !dedupeThreadRows ||
            !incomingConversation.contact_inbox_id ||
            shouldRefreshThreadListRowActivity(
              existingConversation,
              incomingConversation
            );

          if (shouldReplace) {
            newAllConversations[indexInCurrentList] = incomingConversation;
          }
        } else {
          newAllConversations[indexInCurrentList] = {
            ...incomingConversation,
            allMessagesLoaded: existingConversation.allMessagesLoaded,
            messages: existingConversation.messages,
            dataFetched: existingConversation.dataFetched,
            hasNewerMessages: existingConversation.hasNewerMessages,
            newerMessagesCursor: existingConversation.newerMessagesCursor,
            thread_separators: existingConversation.thread_separators,
          };
        }
      }
    });

    if (dedupeThreadRows) {
      newAllConversations =
        dedupeConversationsByContactInbox(newAllConversations);
    }

    _state.allConversations = newAllConversations;
  },
  [types.REPLACE_CONVERSATION_LIST](_state, payload) {
    const selectedConversation = getConversationById(_state)(
      _state.selectedChatId
    );
    _state.allConversations = selectedConversation
      ? [selectedConversation]
      : [];
    mutations[types.SET_ALL_CONVERSATION](_state, payload);
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data.length) return;

    const chat = getConversationById(_state)(id);
    if (!chat) return;

    const existingMessages = chat.messages || [];
    const existingIds = new Set(existingMessages.map(message => message.id));
    const incomingMessages = data.filter(message => {
      if (existingIds.has(message.id)) return false;
      existingIds.add(message.id);
      return true;
    });

    if (!incomingMessages.length) return;

    chat.messages = [...incomingMessages, ...existingMessages];
    if (chat.contact_inbox_id || chat.meta?.contact_inbox_id) {
      chat.messages.sort((a, b) => a.id - b.id);
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_THREAD_METADATA](
    _state,
    {
      id,
      contactInboxId,
      activeConversationId,
      threadSeparators,
      mergeSeparators,
      activeConversationMeta,
      newerMessagesCursor,
      hasNewerMessages,
    }
  ) {
    const chat = getListRowByThreadConversationId(_state.allConversations, id);
    if (!chat) return;

    chat.contact_inbox_id = contactInboxId;
    chat.active_conversation_id = activeConversationId;
    if (newerMessagesCursor !== undefined) {
      chat.newerMessagesCursor = newerMessagesCursor;
    }
    if (hasNewerMessages !== undefined) {
      chat.hasNewerMessages = hasNewerMessages;
    }

    if (activeConversationMeta) {
      if (activeConversationMeta.assignee !== undefined) {
        chat.meta.assignee = activeConversationMeta.assignee;
      }
      if (activeConversationMeta.team !== undefined) {
        chat.meta.team = activeConversationMeta.team;
      }
      if (activeConversationMeta.priority !== undefined) {
        chat.priority = activeConversationMeta.priority;
      }
      if (activeConversationMeta.status) {
        chat.status = activeConversationMeta.status;
      }
      if (activeConversationMeta.aggregate_status) {
        chat.thread_aggregate_status = activeConversationMeta.aggregate_status;
      }
      if (activeConversationMeta.labels) {
        chat.labels = activeConversationMeta.labels;
      }
      if (activeConversationMeta.snoozed_until !== undefined) {
        chat.snoozed_until = activeConversationMeta.snoozed_until;
      }
      if (activeConversationMeta.sla_policy_id !== undefined) {
        chat.sla_policy_id = activeConversationMeta.sla_policy_id;
      }
      if (activeConversationMeta.custom_attributes) {
        chat.custom_attributes = activeConversationMeta.custom_attributes;
      }
      if (activeConversationMeta.unread_count !== undefined) {
        chat.thread_unread_count = activeConversationMeta.unread_count;
      }
      if (activeConversationMeta.thread_last_activity_at) {
        chat.thread_last_activity_at =
          activeConversationMeta.thread_last_activity_at;
        chat.last_activity_at = activeConversationMeta.thread_last_activity_at;
        chat.timestamp = activeConversationMeta.thread_last_activity_at;
      }
    }

    if (mergeSeparators) {
      const existing = chat.thread_separators || [];
      const existingIds = new Set(
        existing.map(separator => separator.before_message_id)
      );
      const newSeparators = (threadSeparators || []).filter(
        separator => !existingIds.has(separator.before_message_id)
      );
      chat.thread_separators = [...newSeparators, ...existing];
    } else {
      chat.thread_separators = threadSeparators || [];
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee, assigneeType }) {
    const chat = getListRowByThreadConversationId(
      _state.allConversations,
      conversationId
    );
    if (chat) {
      chat.meta.assignee = assignee;
      const inferredAssigneeType = assignee ? 'User' : null;
      chat.meta.assignee_type =
        assigneeType === undefined ? inferredAssigneeType : assigneeType;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const chat = getListRowByThreadConversationId(
      _state.allConversations,
      conversationId
    );
    if (chat) {
      chat.meta.team = team;
    }
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const index = findListRowIndexByThread(_state.allConversations, {
      conversationId,
    });
    if (index === -1) return;

    const chat = _state.allConversations[index];
    if (chat.contact_inbox_id) {
      Object.assign(
        chat,
        applyThreadListActivity(chat, { last_activity_at: lastActivityAt })
      );
    } else {
      chat.last_activity_at = lastActivityAt;
      chat.timestamp = lastActivityAt;
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const chat = getListRowByThreadConversationId(
      _state.allConversations,
      conversationId
    );
    if (chat) {
      chat.priority = priority;
    }
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = getListRowByThreadConversationId(
      _state.allConversations,
      conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const index = findListRowIndexByThread(_state.allConversations, {
      conversationId,
    });
    if (index === -1) return;

    const conversation = _state.allConversations[index];
    if (
      Number(conversationId) !== Number(conversation.id) &&
      Number(conversationId) !== Number(conversation.active_conversation_id)
    ) {
      return;
    }

    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
    if (conversation.contact_inbox_id) {
      conversation.thread_aggregate_status = status;
    }
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const contactInboxId = message.conversation?.contact_inbox_id;
    const chatIndex = findListRowIndexByThread(allConversations, {
      conversationId,
      contactInboxId,
    });
    if (chatIndex === -1) return;

    const chat = allConversations[chatIndex];
    if (!chat.messages) {
      chat.messages = [];
    }

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;

      if (chat.contact_inbox_id) {
        const isActiveThreadOpen =
          selectedChatId === chat.id ||
          selectedChatId === conversationId ||
          chat.active_conversation_id === conversationId;

        if (
          message.message_type === MESSAGE_TYPE.INCOMING &&
          !isActiveThreadOpen
        ) {
          chat.thread_unread_count =
            (chat.thread_unread_count ?? chat.unread_count ?? 0) + 1;
        } else if (chat.thread_unread_count !== undefined) {
          chat.thread_unread_count = unreadCount;
        }

        if (
          message.message_type !== MESSAGE_TYPE.ACTIVITY &&
          message.content_type !== CONTENT_TYPES.INPUT_CSAT
        ) {
          Object.assign(
            chat,
            applyThreadListActivity(chat, {
              last_activity_at: message.created_at,
            })
          );
        }
        if (conversationId) {
          chat.active_conversation_id = conversationId;
          const messageStatus = message.conversation?.status;
          if (THREAD_ACTIVE_STATUSES.includes(messageStatus)) {
            chat.status = messageStatus;
            chat.thread_aggregate_status = messageStatus;
          }
        }
      } else if (
        message.message_type !== MESSAGE_TYPE.ACTIVITY &&
        message.content_type !== CONTENT_TYPES.INPUT_CSAT
      ) {
        chat.timestamp = message.created_at;
      }

      if (selectedChatId === chat.id || selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.push(conversation);
    }
  },

  [types.UPSERT_THREAD_LIST_ROW](_state, conversation) {
    const { allConversations } = _state;
    const contactInboxId = conversation.contact_inbox_id;

    if (!contactInboxId) {
      if (!allConversations.some(c => c.id === conversation.id)) {
        allConversations.push(conversation);
      }
      return;
    }

    const index = allConversations.findIndex(
      row => Number(row.contact_inbox_id) === Number(contactInboxId)
    );

    if (index === -1) {
      allConversations.push(conversation);
      return;
    }

    const existing = allConversations[index];
    const newActivity = conversation.last_activity_at || 0;
    const existingActivity = existing.last_activity_at || 0;
    const adoptIncomingId = shouldAdoptThreadListRowId(
      existing,
      conversation,
      _state.selectedChatId
    );

    if (newActivity >= existingActivity) {
      allConversations[index] = {
        ...conversation,
        id: adoptIncomingId ? conversation.id : existing.id,
        ...(existing.dataFetched && existing.messages?.length
          ? {
              messages: existing.messages,
              thread_separators: existing.thread_separators,
              dataFetched: existing.dataFetched,
              allMessagesLoaded: existing.allMessagesLoaded,
              hasNewerMessages: existing.hasNewerMessages,
              newerMessagesCursor: existing.newerMessagesCursor,
            }
          : {}),
        active_conversation_id: resolveThreadActiveConversationId(
          conversation,
          existing
        ),
        thread_aggregate_status:
          conversation.thread_aggregate_status ??
          existing.thread_aggregate_status,
        thread_unread_count:
          conversation.thread_unread_count ?? existing.thread_unread_count,
      };
      return;
    }

    allConversations[index] = {
      ...existing,
      active_conversation_id: resolveThreadActiveConversationId(
        conversation,
        existing
      ),
      thread_aggregate_status:
        conversation.thread_aggregate_status ??
        existing.thread_aggregate_status,
      thread_unread_count:
        conversation.thread_unread_count ?? existing.thread_unread_count,
    };
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = findListRowIndexByThread(allConversations, {
      conversationId: conversation.id,
      contactInboxId: conversation.contact_inbox_id,
    });

    if (index > -1) {
      const selectedConversation = allConversations[index];
      const isThreadListRow = Boolean(selectedConversation.contact_inbox_id);
      if (!isThreadListRow) {
        if (conversation.updated_at < selectedConversation.updated_at) return;
        const { messages, ...updates } = conversation;
        allConversations[index] = { ...selectedConversation, ...updates };
        if (_state.selectedChatId === conversation.id) {
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
        return;
      }
      const isActiveConversationUpdate = isThreadListRow
        ? isActiveThreadConversationUpdate(selectedConversation, conversation)
        : conversation.id === selectedConversation.active_conversation_id ||
          conversation.id === selectedConversation.id;

      if (
        conversation.updated_at < selectedConversation.updated_at &&
        !isActiveConversationUpdate
      ) {
        return;
      }

      const { messages, ...updates } = conversation;
      const newActivity = conversation.last_activity_at || 0;
      const existingActivity = selectedConversation.last_activity_at || 0;
      const shouldRefreshActivity = isThreadListRow
        ? shouldRefreshThreadListRowActivity(selectedConversation, conversation)
        : conversation.id === selectedConversation.id ||
          newActivity >= existingActivity;
      const isRepresentativeUpdate = isThreadListRow
        ? shouldRefreshActivity
        : conversation.id === selectedConversation.id ||
          newActivity >= existingActivity;
      const preserveThreadMessages =
        selectedConversation.dataFetched &&
        (selectedConversation.messages?.length || 0) > 0;
      let messageUpdates = {};
      if (preserveThreadMessages) {
        messageUpdates = {
          messages: selectedConversation.messages,
          thread_separators: selectedConversation.thread_separators,
          dataFetched: selectedConversation.dataFetched,
          allMessagesLoaded: selectedConversation.allMessagesLoaded,
        };
      } else if (messages) {
        messageUpdates = { messages };
      }

      if (isRepresentativeUpdate) {
        const adoptIncomingId = shouldAdoptThreadListRowId(
          selectedConversation,
          conversation,
          _state.selectedChatId
        );

        allConversations[index] = {
          ...selectedConversation,
          ...updates,
          id: adoptIncomingId ? conversation.id : selectedConversation.id,
          ...(isThreadListRow
            ? applyThreadListActivity(selectedConversation, conversation)
            : {}),
          ...messageUpdates,
          active_conversation_id:
            conversation.active_conversation_id ??
            selectedConversation.active_conversation_id,
          thread_aggregate_status:
            conversation.thread_aggregate_status ??
            selectedConversation.thread_aggregate_status,
          thread_unread_count:
            conversation.thread_unread_count ??
            selectedConversation.thread_unread_count,
        };
      } else if (isActiveConversationUpdate) {
        allConversations[index] = {
          ...selectedConversation,
          id: selectedConversation.id,
          ...(isThreadListRow
            ? applyThreadListActivity(selectedConversation, conversation)
            : {}),
          meta: {
            ...selectedConversation.meta,
            ...conversation.meta,
          },
          priority: conversation.priority ?? selectedConversation.priority,
          status: conversation.status ?? selectedConversation.status,
          snoozed_until:
            conversation.snoozed_until ?? selectedConversation.snoozed_until,
          labels: conversation.labels ?? selectedConversation.labels,
          sla_policy_id:
            conversation.sla_policy_id ?? selectedConversation.sla_policy_id,
          custom_attributes:
            conversation.custom_attributes ??
            selectedConversation.custom_attributes,
          active_conversation_id:
            conversation.active_conversation_id ??
            selectedConversation.active_conversation_id,
          thread_aggregate_status:
            conversation.thread_aggregate_status ??
            selectedConversation.thread_aggregate_status,
          thread_unread_count:
            conversation.thread_unread_count ??
            selectedConversation.thread_unread_count,
        };
      } else {
        allConversations[index] = {
          ...selectedConversation,
          active_conversation_id:
            conversation.active_conversation_id ??
            selectedConversation.active_conversation_id,
          thread_aggregate_status:
            conversation.thread_aggregate_status ??
            selectedConversation.thread_aggregate_status,
          thread_unread_count:
            conversation.thread_unread_count ??
            selectedConversation.thread_unread_count,
        };
      }

      if (
        _state.selectedChatId === selectedConversation.id &&
        messages?.length &&
        !preserveThreadMessages &&
        !(isActiveConversationUpdate && selectedConversation.dataFetched)
      ) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      if (![MENTION, PARTICIPATING].includes(conversationType)) {
        _state.allConversations.push(conversation);
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const index = findListRowIndexByThread(_state.allConversations, {
      conversationId: id,
    });
    if (index === -1) return;

    const chat = _state.allConversations[index];
    chat.agent_last_seen_at = lastSeen;
    chat.unread_count = unreadCount;
    if (chat.thread_unread_count !== undefined) {
      chat.thread_unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getListRowByThreadConversationId(
      _state.allConversations,
      payload.id
    );
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const chat = getListRowByThreadConversationId(
      _state.allConversations,
      conversationId
    );
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](
    _state,
    { conversationId, callStatus, callSid }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const message = (chat.messages || []).find(
      m =>
        m.content_type === CONTENT_TYPES.VOICE_CALL &&
        m.call?.provider_call_id === callSid
    );
    if (!message?.call) return;

    message.call = { ...message.call, status: callStatus };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.SET_CONVERSATION_FILTERS_SORT](_state, sortBy) {
    _state.appliedFiltersSortBy = sortBy;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
    _state.appliedFiltersSortBy = null;
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
